/**
 * Helpers for the WhatsApp Embedded Signup `WA_EMBEDDED_SIGNUP` postMessage.
 *
 * Meta posts a `WA_EMBEDDED_SIGNUP` message during the Embedded Signup flow
 * (on pre-v4 configurations this requires `sessionInfoVersion: '3'` in the
 * `FB.login` extras — see `embeddedSignupLogin.ts`; v4 always sends it). For a
 * completed onboarding it carries the `waba_id` / `phone_number_id`. This is
 * the canonical source of those ids for BOTH standard Cloud API and
 * Coexistence (CoEx) onboardings — in CoEx the WABA is NOT present in the
 * exchanged token's `granular_scopes`, so the backend cannot derive it from
 * `debug_token` and the connection fails with a 500 unless the frontend
 * forwards these ids in the `POST /whatsapp-api/account` body.
 *
 * INT-1216: the same channel also carries abandonment and error-report
 * payloads, which used to be dropped here. Two entry points now exist and the
 * split is deliberate:
 *
 * - `parseEmbeddedSignupSessionInfo` — the narrow, connection-critical read.
 *   Returns the ids or `null`, exactly as before. The CoEx path depends on it.
 * - `parseEmbeddedSignupMessage` — the wide, telemetry read. Classifies the
 *   message by the payload it actually carries.
 *
 * Classification is driven by payload shape, NOT by the `event` string. Meta's
 * documentation describes `CANCEL` as "User abandoned flow or reported error"
 * with two different `data` shapes, so a `CANCEL` carrying `error_code` is an
 * error report and a `CANCEL` carrying `current_step` is an abandonment. The
 * raw `event` string is passed through untouched for the same reason we never
 * map the step onto an enum: Meta ships values it has not documented.
 */

export type tEmbeddedSignupSessionInfo = {
  wabaId: string;
  phoneNumberId: string;
};

type tEmbeddedSignupCommon = {
  /**
   * Raw `event` value from Meta (`FINISH`, `CANCEL`, `ERROR`, …). Never mapped
   * onto an internal enum — Meta emits undocumented values.
   */
  event: string;
  /** Raw `version` from the message envelope; empty string when absent. */
  version: string;
};

/**
 * A Meta Embedded Signup message classified by the payload it carries.
 *
 * - `session` — carries the WABA / phone-number ids (a completed onboarding).
 * - `abandoned` — carries only `current_step`. Meta sends this automatically
 *   for every abandonment, so it is the one signal with full coverage.
 * - `error_report` — carries `error_code` / `error_message` / `session_id`.
 *   Only produced when the customer presses «Reportar error a Leadsales», so
 *   coverage is partial and opt-in by design on Meta's side.
 */
export type tEmbeddedSignupMessage =
  | (tEmbeddedSignupCommon & { kind: 'session' } & tEmbeddedSignupSessionInfo)
  | (tEmbeddedSignupCommon & { kind: 'abandoned'; currentStep: string })
  | (tEmbeddedSignupCommon & {
      kind: 'error_report';
      /** Meta's numeric error code, or `null` when absent/unparseable. */
      errorCode: number | null;
      /** Already localized by Meta — safe to surface to the customer as-is. */
      errorMessage: string;
      /**
       * Meta's own `session_id`. Present ONLY on error reports, which is why
       * abandonment correlation needs an attempt id we issue ourselves.
       */
      metaSessionId: string;
      /** Meta's `timestamp`, kept as the raw string it arrives as. */
      metaTimestamp: string;
    });

// Origins Meta uses to post Embedded Signup messages. Anything else is noise
// from other embedded frames / extensions and must be ignored.
const META_MESSAGE_ORIGINS = [
  'https://www.facebook.com',
  'https://web.facebook.com',
];

function readString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

function readNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  // Meta documents error_code as numeric, but the payload crosses a JSON
  // boundary — tolerate a numeric string rather than losing the code.
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

/**
 * Shared gate for both entry points: verifies the message came from a Meta
 * origin, is JSON-decodable, is a `WA_EMBEDDED_SIGNUP` envelope, and carries an
 * object `data`. This is the security boundary — it must apply identically to
 * the telemetry read and the connection-critical read.
 */
function readSignupEnvelope(
  event: MessageEvent,
): { message: Record<string, unknown>; data: Record<string, unknown> } | null {
  if (!META_MESSAGE_ORIGINS.includes(event.origin)) return null;

  let payload: unknown;
  try {
    payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
  } catch {
    // Non-JSON postMessage noise (other SDKs broadcast plain strings).
    return null;
  }

  if (!payload || typeof payload !== 'object') return null;
  const message = payload as Record<string, unknown>;
  if (message.type !== 'WA_EMBEDDED_SIGNUP') return null;

  const data = message.data;
  if (!data || typeof data !== 'object') return null;

  return { message, data: data as Record<string, unknown> };
}

/**
 * Extracts the WABA / phone-number ids from a window `message` event if (and
 * only if) it is a Meta `WA_EMBEDDED_SIGNUP` payload carrying them. Returns
 * `null` for any unrelated message, non-Meta origin, non-JSON data, or a
 * progress/cancel event without the ids — callers can safely ignore `null`.
 */
export function parseEmbeddedSignupSessionInfo(
  event: MessageEvent,
): tEmbeddedSignupSessionInfo | null {
  const envelope = readSignupEnvelope(event);
  if (!envelope) return null;

  const { data } = envelope;
  const wabaId = typeof data.waba_id === 'string' ? data.waba_id : '';
  const phoneNumberId =
    typeof data.phone_number_id === 'string' ? data.phone_number_id : '';

  // Progress / cancel events carry neither id — nothing to capture here. The
  // telemetry read below is what picks those up.
  if (!wabaId && !phoneNumberId) return null;

  return { wabaId, phoneNumberId };
}

/**
 * Classifies a window `message` event into the Embedded Signup outcome it
 * represents. Returns `null` for anything that is not a Meta Embedded Signup
 * message, and for Embedded Signup messages that carry no usable payload at
 * all (nothing to record).
 */
export function parseEmbeddedSignupMessage(
  event: MessageEvent,
): tEmbeddedSignupMessage | null {
  const envelope = readSignupEnvelope(event);
  if (!envelope) return null;

  const { message, data } = envelope;
  const common: tEmbeddedSignupCommon = {
    event: readString(message.event),
    version: readString(message.version),
  };

  const wabaId = typeof data.waba_id === 'string' ? data.waba_id : '';
  const phoneNumberId =
    typeof data.phone_number_id === 'string' ? data.phone_number_id : '';
  if (wabaId || phoneNumberId) {
    return { ...common, kind: 'session', wabaId, phoneNumberId };
  }

  // Error reports are checked before abandonment: Meta can send both shapes
  // under the same `CANCEL` event name, and the error report is strictly more
  // informative when present.
  const errorCode = readNumber(data.error_code);
  const errorMessage = readString(data.error_message);
  if (errorCode !== null || errorMessage) {
    return {
      ...common,
      kind: 'error_report',
      errorCode,
      errorMessage,
      metaSessionId: readString(data.session_id),
      metaTimestamp: readString(data.timestamp),
    };
  }

  const currentStep = readString(data.current_step);
  if (currentStep) {
    return { ...common, kind: 'abandoned', currentStep };
  }

  return null;
}
